import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { api } from "./api";

/**
 * Der Zustand der Anlage: was zur Inbetriebnahme fehlt und ob sie gesund ist.
 *
 * Beides stand bis zum 04.09.2026 auf der Uebersicht, der Startseite der
 * Geschaeftsleitung. Mit dem Wegfall jener Seite gehoert es in die
 * Einstellungen, denn dort wird eingerichtet, und dort sucht es auch, wer
 * spaeter wissen will, ob die Sicherung noch laeuft.
 *
 * Der urspruengliche Einwand gegen eine eigene Maske gilt weiter: niemand ruft
 * eine Seite auf, um zu erfahren, dass alles in Ordnung ist. Deshalb steht
 * beides oben in den Einstellungen und nicht hinter einem eigenen Reiter, und
 * beides verschwindet, wenn es nichts zu melden gibt.
 */

interface Einrichtungspunkt {
  schluessel: string;
  anzahl: number;
  weg: string;
}

/**
 * Was zur Inbetriebnahme noch fehlt.
 *
 * Diese Liste stand bisher in einem Dokument, das niemand aufmacht. Jetzt
 * fragt die Anwendung selbst nach und zeigt nur, was tatsaechlich fehlt.
 * Ist alles eingerichtet, steht hier gar nichts: eine dauerhafte Meldung
 * "alles erledigt" wird nach drei Tagen nicht mehr gelesen.
 */
export function Einrichtung() {
  const { t } = useTranslation();
  const [punkte, setPunkte] = useState<Einrichtungspunkt[] | null>(null);

  useEffect(() => {
    void api.get<{ punkte: Einrichtungspunkt[] }>("/einrichtung")
      .then((r) => setPunkte(r.punkte))
      .catch(() => setPunkte([]));
  }, []);

  if (!punkte || punkte.length === 0) return null;

  return (
    <>
      <h2 className="mt-10 text-xl font-bold">{t("einrichtung.titel")}</h2>
      <p className="mt-1 text-sm text-leise">{t("einrichtung.hinweis")}</p>
      <ul className="mt-3 space-y-2">
        {punkte.map((p) => (
          <li key={p.schluessel}>
            <Link
              to={p.weg}
              className="flex items-center justify-between gap-3 rounded-card border border-marke bg-white px-4 py-3 hover:bg-grund"
            >
              <span>{t(`einrichtung.${p.schluessel}`, { count: p.anzahl })}</span>
              <span className="shrink-0 text-sm text-leise">{t("einrichtung.hin")}</span>
            </Link>
          </li>
        ))}
      </ul>
    </>
  );
}

interface Systemzustand {
  sicherung: {
    stand: "aktuell" | "veraltet" | "keine" | "unbekannt";
    zeitpunkt: string | null;
    alterStunden: number | null;
    groesse: number | null;
    verschluesselt: boolean;
  };
  speicher: boolean;
  post: boolean;
  standortdaten: {
    frist: number;
    punkte: number;
    aeltester: string | null;
    ueberfaellig: boolean;
  };
}

/**
 * Drei Zeilen darueber, ob die Anlage gesund ist.
 *
 * Sie stehen hier und nicht in einer eigenen Maske, weil niemand eine Maske
 * aufruft, um zu erfahren, dass alles in Ordnung ist. Gesehen wird nur, was
 * ohnehin auf dem Weg liegt.
 *
 * Wichtigste Zeile ist die Sicherung. Sie faellt lautlos aus, und dass sie
 * ausgefallen ist, merkt man sonst genau einmal: an dem Tag, an dem man sie
 * braucht.
 */
export function Anlagenzustand() {
  const { t, i18n } = useTranslation();
  const [zustand, setZustand] = useState<Systemzustand | null>(null);

  useEffect(() => {
    api.get<Systemzustand>("/systemzustand").then(setZustand).catch(() => setZustand(null));
  }, []);

  if (!zustand) return null;

  const s = zustand.sicherung;
  const sicherungText =
    s.stand === "aktuell" || s.stand === "veraltet"
      ? t("system.sicherungStand", {
          zeit: new Date(s.zeitpunkt as string).toLocaleString(i18n.language),
          groesse: megabyte(s.groesse ?? 0)
        })
      : t(`system.sicherung.${s.stand}`);

  return (
    <>
      <h2 className="mt-10 text-xl font-bold">{t("system.titel")}</h2>
      <ul className="mt-3 space-y-2">
        <Zustandszeile
          label={t("system.sicherung")}
          text={sicherungText}
          art={s.stand === "aktuell" ? "gut" : s.stand === "unbekannt" ? "unklar" : "schlecht"}
        />
        {/* In der Sicherung steht die ganze Firma im Klartext, und sie liegt
            dreissig Tage bei einem Anbieter. Ob sie verschluesselt ist, sieht
            sonst niemand: das Protokoll des Sicherungscontainers liest im
            Alltag keiner. */}
        <Zustandszeile
          label={t("system.sicherungSchutz")}
          text={t(s.verschluesselt ? "system.verschluesselt" : "system.unverschluesselt")}
          art={s.verschluesselt ? "gut" : "schlecht"}
        />
        <Zustandszeile
          label={t("system.speicher")}
          text={t(zustand.speicher ? "system.eingerichtet" : "system.fehlt")}
          art={zustand.speicher ? "gut" : "schlecht"}
        />
        <Zustandszeile
          label={t("system.post")}
          text={t(zustand.post ? "system.eingerichtet" : "system.fehlt")}
          art={zustand.post ? "gut" : "schlecht"}
        />
        {/* Steht hier rot, sammeln sich Standortdaten ueber die Frist hinaus
            an. Das meldet sich sonst nie von selbst, und es ist genau der
            Punkt, an dem eine Kontrolle nachfragt. */}
        <Zustandszeile
          label={t("system.standortdaten")}
          text={
            zustand.standortdaten.ueberfaellig
              ? t("system.standortUeberfaellig", { tage: zustand.standortdaten.frist })
              : t("system.standortStand", {
                  anzahl: zustand.standortdaten.punkte,
                  tage: zustand.standortdaten.frist
                })
          }
          art={zustand.standortdaten.ueberfaellig ? "schlecht" : "gut"}
        />
      </ul>
    </>
  );
}

function megabyte(bytes: number): string {
  return `${(bytes / 1_048_576).toFixed(1)} MB`;
}

function Zustandszeile({
  label, text, art
}: { label: string; text: string; art: "gut" | "unklar" | "schlecht" }) {
  /*
   * Die Farbe allein traegt die Aussage nicht: wer rot und gruen nicht
   * unterscheidet, liest sonst nichts. Deshalb steht neben dem Punkt immer
   * ein Satz, der dasselbe sagt.
   */
  const farbe = art === "gut" ? "bg-green-600" : art === "unklar" ? "bg-leise" : "bg-red-600";
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-card bg-white p-4">
      <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${farbe}`} aria-hidden="true" />
      <span className="font-semibold">{label}</span>
      <span className="text-sm text-leise">{text}</span>
    </li>
  );
}
